import { useState } from 'react';
import { toast } from 'sonner';
import { X, Ban, Plus } from 'lucide-react';
import api from '../../lib/api';
import { firstName } from '../../lib/play';

/** "Don't pair together" rules — hard constraint for the shuffler. */
export default function AvoidPairsEditor({ groupId, pairs, players, nameOf, canEdit, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [first, setFirst] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (next) => {
    setBusy(true);
    try {
      const r = await api.put(`/groups/${groupId}/play/avoid-pairs`, { avoid_pairs: next });
      onChanged(r.data.avoid_pairs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save rule');
    } finally {
      setBusy(false);
    }
  };

  const pick = (id) => {
    if (!first) { setFirst(id); return; }
    if (first === id) { setFirst(null); return; }
    const key = [first, id].sort().join('|');
    if (pairs.some((p) => [...p].sort().join('|') === key)) { toast('That rule already exists'); setFirst(null); return; }
    save([...pairs, [first, id]]);
    setFirst(null);
    setAdding(false);
  };

  return (
    <div className="rounded-xl border p-3" data-testid="avoid-pairs-editor">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold inline-flex items-center gap-1"><Ban className="w-3.5 h-3.5 text-rose-500" /> Don't pair together</p>
          <p className="text-xs text-muted-foreground">Shuffle will never put these two on the same team (e.g. your two strongest).</p>
        </div>
        {canEdit && !adding && (
          <button type="button" onClick={() => setAdding(true)} className="text-xs font-semibold text-primary inline-flex items-center gap-1 shrink-0" data-testid="avoid-pair-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add rule
          </button>
        )}
      </div>
      {pairs.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="avoid-pairs-list">
          {pairs.map((p) => (
            <span key={p.join('|')} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-xs font-semibold" data-testid={`avoid-pair-${p.join('-')}`}>
              {firstName(nameOf(p[0]))} × {firstName(nameOf(p[1]))}
              {canEdit && (
                <button type="button" disabled={busy} onClick={() => save(pairs.filter((q) => q.join('|') !== p.join('|')))} className="w-5 h-5 rounded-full hover:bg-rose-500/20 inline-flex items-center justify-center" data-testid={`avoid-pair-remove-${p.join('-')}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {adding && (
        <div className="mt-2" data-testid="avoid-pair-picker">
          <p className="text-xs text-muted-foreground mb-1.5">{first ? `Now tap who ${firstName(nameOf(first))} should NOT partner with` : 'Tap the first player'}</p>
          <div className="flex flex-wrap gap-1.5">
            {players.map((id) => (
              <button key={id} type="button" onClick={() => pick(id)} data-testid={`avoid-pick-${id}`}
                className={`h-8 px-3 rounded-full border text-xs font-semibold ${first === id ? 'bg-rose-500/15 border-rose-500/60' : 'bg-background'}`}>
                {firstName(nameOf(id))}
              </button>
            ))}
            <button type="button" onClick={() => { setAdding(false); setFirst(null); }} className="h-8 px-3 rounded-full text-xs text-muted-foreground" data-testid="avoid-pair-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
